/// «Огляд» — every word of the page, in its own file so that it merges cleanly
/// next to other sections (uk.ts carries one line for it). The hints under
/// the figures are the definitions of specs/reports.md §8.1 in one line each.

const plural = (n: number, one: string, few: string, many: string): string => {
  const mod10 = n % 10
  const mod100 = n % 100
  if (mod10 === 1 && mod100 !== 11) return one
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return few
  return many
}

const sales = (n: number) => `${n} ${plural(n, 'продаж', 'продажі', 'продажів')}`

export const overviewUk = {
  nav: 'Огляд',
  title: 'Огляд',
  subtitle: 'Головні цифри клубу за період — люди й гроші.',
  period: 'Період',
  periods: { thisMonth: 'Цей місяць', lastMonth: 'Минулий місяць', month: 'Останні 30 днів', year: 'Рік' } as Record<string, string>,
  club: 'Клуб',
  allClubs: 'Усі клуби',

  people: 'Клієнти',
  base: 'Загальна база',
  added: (n: number) => (n === 0 ? 'Нових за період немає' : `+${n} ${plural(n, 'новий', 'нові', 'нових')} за період`),
  baseHint: 'Усі клієнти клубу, крім архівних',
  members: 'Постійні клієнти',
  /// «на 30 жовтня» — the period's last day: today for «Цей місяць».
  membersOn: (day: string) => `на ${day}`,
  paused: (n: number) => `${n} на заморозці`,
  membersHint: 'Мають чинний абонемент — активний чи заморожений',
  visitors: 'Відвідували',
  visits: (n: string, count: number) => (count === 0 ? 'Входів не було' : `${n} ${plural(count, 'вхід', 'входи', 'входів')}`),
  visitorsHint: 'Різні люди, що пройшли вхід',

  money: 'Гроші',
  revenue: 'Виручка',
  refunded: (amount: string) => `повернено ${amount}`,
  revenueHint: 'Оплати мінус повернення, за днями клубу',
  sales: 'Продажі',
  salesHint: 'Оплачені абонементи, послуги й товари',
  average: 'Середній чек',
  // Not a receipt yet (specs/reports.md §8.3): a pass and a bottle of water
  // at one visit are two sales until the cash desk writes receipts.
  averageHint: 'Виручка ÷ продажі. Коли зʼявиться каса — рахуватимемо за чеками',

  byStream: 'За напрямами',
  streams: { passes: 'Абонементи', services: 'Послуги', goods: 'Товари' } as Record<string, string>,
  streamHints: {
    passes: 'Каса й застосунок',
    services: 'Оплачені записи',
    goods: 'Продажі з полиці',
  } as Record<string, string>,
  columns: { stream: 'Напрям', revenue: 'Виручка', sales: 'Продажі', average: 'Середній чек', refunds: 'Повернення' },
  /// On a phone the figures are words in the row's second line.
  salesShort: sales,
  averageShort: 'чек',
  /// Why a figure is «—» (specs/reports.md R13).
  gaps: { forbidden: 'Немає доступу', failed: 'Не вдалося завантажити' } as Record<string, string>,
  streamsFootnote:
    'Абонементи — продажі на касі й у застосунку, мінус повернення оплат із застосунку; видані за 0 ₴ — не продажі. Послуги — оплачені записи мінус повернення. Товари — продажі з «Товари → Продажі».',

  passes: 'Абонементи',
  /// «На касі 40 продажів на 52 400 ₴, у застосунку 6 продажів на 7 200 ₴».
  channels: (deskCount: number, desk: string, appCount: number, app: string) =>
    `На касі ${sales(deskCount)} на ${desk}, у застосунку ${sales(appCount)} на ${app}`,
  appRefunds: (n: number, amount: string) => `Повернено оплат із застосунку: ${n} на ${amount}`,
  free: (n: number) =>
    `${n} ${plural(n, 'абонемент видано', 'абонементи видано', 'абонементів видано')} за 0 ₴ — це не продажі`,
  plans: 'Тарифи',
  planColumns: { name: 'Тариф', sales: 'Продажі', revenue: 'Виручка' },
  noPlans: 'За цей період абонементів не продавали',
  morePlans: (n: number) => `і ще ${n} ${plural(n, 'тариф', 'тарифи', 'тарифів')}`,

  /// With one club chosen (specs/reports.md §11).
  unassigned: (count: number, amount: string) =>
    `Ще ${sales(count)} на ${amount} не належать жодному клубу — мережеві абонементи й заняття без клубу. Вони враховані в «Усі клуби».`,
  /// A total that leaves a stream out says which (R13).
  missing: (streams: string) => `Разом пораховано без: ${streams}.`,
  missingStream: (stream: string, why: string) => `${stream} — ${why.toLowerCase()}`,
  blank: 'Тут зʼявляться цифри клубу, щойно ви внесете клієнтів і продасте абонемент, послугу чи товар.',
  noAccess: 'Огляд бачить роль із правом «Звіти» або «Фінанси».',
}
